<script setup lang="ts">
import { PV } from "@/widgets";
import { watchEffect } from "vue";
import { defineActions, defineState } from "../common";
import { Buttons } from "../icons";
import { FormGuide, FormHead, FormMessage } from "../internal";
import { type BaseProps, defaultProps } from "../props";

type ModelType = Date;
interface PropsType extends BaseProps<ModelType> {
    formId?: string;
    placeholder?: string;
    min?: Date;
    max?: Date;
}

const props = withDefaults(defineProps<PropsType>(), {
    ...defaultProps<ModelType>(),
    value: undefined,
});

const slots = defineSlots<{
    icon?: () => unknown;
}>();

const model = defineModel<ModelType | undefined>({
    default: undefined,
});

const state = defineState({
    model: model,
    props: props,
    hasValue: () => model.value !== undefined,
});

const actions = defineActions(model, props, state);

watchEffect(() => {
    if (model.value === null) {
        model.value = undefined;
    }
});
</script>

<template>
    <div class="flex flex-col gap-1">
        <FormHead :actions="actions" :param="props" :state="state" />

        <template v-if="!state.editMode">
            <PV.IconField>
                <PV.InputIcon v-if="slots.icon !== undefined" class="-mt-2.5!">
                    <slot name="icon"></slot>
                </PV.InputIcon>
                <PV.DatePicker v-model="model" class="rns-input-static" disabled fluid timeOnly />
                <Buttons.ExtUpdate v-if="state.updatable" @click="actions.onUpdate" />
            </PV.IconField>
        </template>
        <template v-else>
            <PV.IconField>
                <PV.InputIcon v-if="slots.icon !== undefined" class="-mt-2.5!">
                    <slot name="icon"></slot>
                </PV.InputIcon>
                <PV.DatePicker
                    v-model="model"
                    class="rns-input-active"
                    fluid
                    :form="props.formId"
                    :inputId="state.id"
                    :invalid="state.hasError"
                    :maxDate="props.max"
                    :minDate="props.min"
                    :placeholder="props.placeholder ?? `Pilih ${label}`"
                    :required="props.required"
                    timeOnly
                    @change="actions.onValidate"
                    @keyup.enter.prevent="actions.onSubmit"
                />
                <Buttons.ExtReset v-if="state.clearable && state.hasValue" @click="actions.onReset" />
            </PV.IconField>
        </template>

        <FormGuide :state="state" :text="props.guideText" />
        <FormMessage :state="state" />
    </div>
</template>

<style scoped>
@reference "@/styles/main.css";

.rns-input-active :deep(.p-datepicker-input) {
    @apply hover:cursor-pointer;
}

.rns-input-static :deep(.p-datepicker-input) {
    @apply text-gray-700! bg-transparent! shadow-none! px-1! pt-2.25!;
    @apply rns-g-static-input;
}
</style>
